import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import {
  PHONE_COUNTRIES,
  countryFlag,
  countryOfLocale,
  countryOfTimeZone,
  normalizePhone,
  splitPhone,
  type CountryCode,
} from "@mubin/contracts";

/**
 * The visitor's own country: from the device's time zone (where they are), else the browser's language, else
 * Saudi Arabia. The language alone misleads: Arabic is often set as ar-AE wherever the visitor lives.
 */
export function defaultCountry(): CountryCode {
  return (
    countryOfTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone) ??
    countryOfLocale(navigator.language) ??
    countryOfLocale(Intl.DateTimeFormat().resolvedOptions().locale) ??
    "SA"
  );
}

const bare = (s: string) => s.replace(/[أإآ]/g, "ا").replace(/ة/g, "ه").toLowerCase();

/**
 * A phone number with its country (owner request 2026-10-21, like Adahina): the country code comes from the
 * chosen country (the visitor's own by default, changeable, searchable), and the number may be typed in any
 * form: 05…, 5…, 9665…, +9665…, 009665…. `onChange` gets the number in E.164 when it is valid for the
 * country, otherwise what was typed (the server says what is wrong).
 */
export function PhoneField({
  id,
  value,
  onChange,
  required,
  autoFocus,
}: {
  id: string;
  value: string;
  onChange(phone: string): void;
  required?: boolean;
  autoFocus?: boolean;
}) {
  const initial = splitPhone(value);
  const [country, setCountry] = useState<CountryCode>(initial?.country ?? defaultCountry());
  const [national, setNational] = useState(initial?.national ?? value);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const box = useRef<HTMLDivElement>(null);
  const emitted = useRef(value);
  const emit = (v: string) => {
    emitted.current = v;
    onChange(v);
  };

  // A value set from outside (a prefill or a reset) replaces what is shown.
  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    const parts = splitPhone(value);
    if (parts) setCountry(parts.country);
    setNational(parts?.national ?? value);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const dial = PHONE_COUNTRIES.find((c) => c.iso === country)?.dial ?? "966";
  const list = useMemo(() => {
    const q = bare(query.trim().replace(/^\+/, ""));
    return q
      ? PHONE_COUNTRIES.filter(
          (c) => bare(c.name).includes(q) || c.dial.startsWith(q) || c.iso.toLowerCase() === q,
        )
      : PHONE_COUNTRIES;
  }, [query]);

  function type(text: string) {
    const e164 = normalizePhone(text, country);
    const parts = splitPhone(e164);
    // A full international number pasted for another country switches the country.
    if (parts && parts.country !== country && /^(\+|00)/.test(text.trim())) {
      setCountry(parts.country);
      setNational(parts.national);
    } else setNational(text);
    emit(e164);
  }

  function pick(iso: CountryCode) {
    setCountry(iso);
    setOpen(false);
    setQuery("");
    emit(normalizePhone(national, iso));
  }

  return (
    <div className="phone-field" ref={box}>
      <input
        id={id}
        dir="ltr"
        inputMode="tel"
        autoComplete="tel-national"
        placeholder={country === "SA" ? "05XXXXXXXX" : ""}
        value={national}
        onChange={(e) => type(e.target.value)}
        required={required}
        autoFocus={autoFocus}
      />
      <button
        type="button"
        className="phone-country"
        aria-label={`رمز الدولة +${dial}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="ltr">+{dial}</span>
        <span aria-hidden="true">{countryFlag(country)}</span>
        <ChevronDown size={14} strokeWidth={1.75} />
      </button>
      {open && (
        <div className="phone-menu" role="listbox" aria-label="الدولة">
          <label className="phone-search">
            <Search size={16} strokeWidth={1.75} />
            <input
              autoFocus
              placeholder="ابحث باسم الدولة أو الرمز"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          <ul>
            {list.map((c) => (
              <li key={c.iso}>
                <button
                  type="button"
                  role="option"
                  aria-selected={c.iso === country}
                  className={c.iso === country ? "on" : ""}
                  onClick={() => pick(c.iso)}
                >
                  <span aria-hidden="true">{countryFlag(c.iso)}</span>
                  <span className="phone-name">{c.name}</span>
                  <span className="ltr muted">+{c.dial}</span>
                </button>
              </li>
            ))}
            {list.length === 0 && <li className="muted small phone-empty">لا نتائج</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
